/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { Auth0AccountRole } from "../models/Auth0AccountRole";
import type { DefaultPaymentPlan } from "../models/DefaultPaymentPlan";
import type { FormDetail } from "../models/FormDetail";
import type { FormFieldCatalog } from "../models/FormFieldCatalog";
import type { FormFilledRow } from "../models/FormFilledRow";
import type { FormResponseDetail } from "../models/FormResponseDetail";
import type { FormSendRequest } from "../models/FormSendRequest";
import type { FormSendResponse } from "../models/FormSendResponse";
import type { FormSummary } from "../models/FormSummary";
import type { FormUpdate } from "../models/FormUpdate";
import type { FormWaitingRow } from "../models/FormWaitingRow";
import type { MyOpenForm } from "../models/MyOpenForm";
import type { PersonFormRow } from "../models/PersonFormRow";

import type { CancelablePromise } from "../core/CancelablePromise";
import { OpenAPI } from "../core/OpenAPI";
import { request as __request } from "../core/request";

export class FormsService {
  /**
   * Get Forms
   * Every form of the organization with its sent and filled-out counts.
   * @returns FormSummary Successful Response
   * @throws ApiError
   */
  public static getForms({
    orgId,
  }: {
    orgId: number;
  }): CancelablePromise<Array<FormSummary>> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/forms/organizations/{org_id}",
      path: {
        org_id: orgId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Create Form
   * A new form with a free name and the usual starting fields.
   * @returns FormDetail Successful Response
   * @throws ApiError
   */
  public static createForm({
    orgId,
  }: {
    orgId: number;
  }): CancelablePromise<FormDetail> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/forms/organizations/{org_id}",
      path: {
        org_id: orgId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Get Default Payment Plan
   * The plan a student with no plan is put on when a payment method is saved for their family.
   * @returns DefaultPaymentPlan Successful Response
   * @throws ApiError
   */
  public static getDefaultPaymentPlan({
    orgId,
  }: {
    orgId: number;
  }): CancelablePromise<DefaultPaymentPlan> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/forms/organizations/{org_id}/default_payment_plan",
      path: {
        org_id: orgId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Update Default Payment Plan
   * Save the default plan. It only applies to payment methods saved from now on.
   * @returns DefaultPaymentPlan Successful Response
   * @throws ApiError
   */
  public static updateDefaultPaymentPlan({
    orgId,
    requestBody,
  }: {
    orgId: number;
    requestBody: DefaultPaymentPlan;
  }): CancelablePromise<DefaultPaymentPlan> {
    return __request(OpenAPI, {
      method: "PUT",
      url: "/forms/organizations/{org_id}/default_payment_plan",
      path: {
        org_id: orgId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Get Form Field Catalog
   * The fields a form can ask parents, students and tutors.
   * @returns FormFieldCatalog Successful Response
   * @throws ApiError
   */
  public static getFormFieldCatalog({
    orgId,
  }: {
    orgId: number;
  }): CancelablePromise<FormFieldCatalog> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/forms/organizations/{org_id}/field_catalog",
      path: {
        org_id: orgId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Send Form To People
   * Send a form by text and/or email. Each person gets their own link, which
   * works for two weeks after the last send; sending again re-uses it.
   * @returns FormSendResponse Successful Response
   * @throws ApiError
   */
  public static sendFormToPeople({
    orgId,
    requestBody,
  }: {
    orgId: number;
    requestBody: FormSendRequest;
  }): CancelablePromise<FormSendResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/forms/organizations/{org_id}/send",
      path: {
        org_id: orgId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Get Person Forms
   * A person's forms, for the Forms section of their page.
   * @returns PersonFormRow Successful Response
   * @throws ApiError
   */
  public static getPersonForms({
    orgId,
    personType,
    personId,
  }: {
    orgId: number;
    personType: Auth0AccountRole;
    personId: number;
  }): CancelablePromise<Array<PersonFormRow>> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/forms/organizations/{org_id}/person_forms",
      path: {
        org_id: orgId,
      },
      query: {
        person_type: personType,
        person_id: personId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Get My Open Forms
   * The forms sent to the logged-in parent that she can still fill out, with
   * their links, for her dashboard. Only her own, in her own organizations.
   * @returns MyOpenForm Successful Response
   * @throws ApiError
   */
  public static getMyOpenForms(): CancelablePromise<Array<MyOpenForm>> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/forms/me/open",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Get Form Response
   * A filled-out form with every answer (also for deleted forms).
   * @returns FormResponseDetail Successful Response
   * @throws ApiError
   */
  public static getFormResponse({
    responseId,
  }: {
    responseId: number;
  }): CancelablePromise<FormResponseDetail> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/forms/responses/{response_id}",
      path: {
        response_id: responseId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Get Form
   * A form and its definition, for the builder.
   * @returns FormDetail Successful Response
   * @throws ApiError
   */
  public static getForm({
    formId,
  }: {
    formId: number;
  }): CancelablePromise<FormDetail> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/forms/{form_id}",
      path: {
        form_id: formId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Update Form
   * Save the builder. The last save wins.
   * @returns FormDetail Successful Response
   * @throws ApiError
   */
  public static updateForm({
    formId,
    requestBody,
  }: {
    formId: number;
    requestBody: FormUpdate;
  }): CancelablePromise<FormDetail> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/forms/{form_id}",
      path: {
        form_id: formId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Delete Form
   * Hide the form. Its filled-out forms stay on people's pages.
   * @returns FormSummary Successful Response
   * @throws ApiError
   */
  public static deleteForm({
    formId,
  }: {
    formId: number;
  }): CancelablePromise<FormSummary> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/forms/{form_id}",
      path: {
        form_id: formId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Duplicate Form
   * A new form to start from this one: "Copy of <name>" with the same
   * sections and fields. Nothing sent or filled out comes with it, and a
   * website inquiry copy gets its own public link.
   * @returns FormDetail Successful Response
   * @throws ApiError
   */
  public static duplicateForm({
    formId,
  }: {
    formId: number;
  }): CancelablePromise<FormDetail> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/forms/{form_id}/duplicate",
      path: {
        form_id: formId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Get Form Responses
   * Everyone who filled out the form, newest first.
   * @returns FormFilledRow Successful Response
   * @throws ApiError
   */
  public static getFormResponses({
    formId,
  }: {
    formId: number;
  }): CancelablePromise<Array<FormFilledRow>> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/forms/{form_id}/responses",
      path: {
        form_id: formId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Get Form Waiting List
   * Everyone who was sent the form and has not filled it out.
   * @returns FormWaitingRow Successful Response
   * @throws ApiError
   */
  public static getFormWaitingList({
    formId,
  }: {
    formId: number;
  }): CancelablePromise<Array<FormWaitingRow>> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/forms/{form_id}/waiting",
      path: {
        form_id: formId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }
}
